import { observable, action, runInAction, makeObservable } from "mobx";
import { projectsDataStore } from "Stores/ProjectsStore";
import { userInfoStore } from "Stores/UserInfoStore";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { CoreStore, handleStoreState } from "Stores/RESTStore/CoreStore";
import { ActiveFilters, FilterConfig } from "Components/ScrollableTable/ScrollableTable";

const initialFilters: API_TaskFilter = {
  createdByMe: false,
  delegatedByMe: false,
  myTasks: true,
  openInProgress: true,
  open: false,
  dueToday: false,
  dueTomorrow: false,
  finished: false,
  responsibles: null,
};

export class TasksDataStore extends CoreStore {
  PAGE_SIZE = 50;
  activeFilters: API_TaskFilter = Object.assign({}, initialFilters);
  taskListId: number = null;
  isPrivate: boolean = false;
  data: Array<API_TaskData> = [];
  total: number = 0;
  responsibleOptions: FilterConfig[] = [];
  sortBy: keyof API_TaskData = "name";
  sortOrder: "ASC" | "DESC" = "ASC";

  constructor() {
    super();

    makeObservable(this, {
      data: observable,
      total: observable,
      activeFilters: observable,
      taskListId: observable,
      isPrivate: observable,
      toggleFilter: action.bound,
      createTask: action.bound,
      updateTaskInList: action.bound,
      taskIsEditable: action.bound,
      checkOwner: action.bound,
      clear: action.bound,
      setDefaultFilters: action.bound,
      loadMore: action.bound,
      fetch: action.bound,
      setFilters: action.bound,
      setSorting: action.bound,
    });

    this.activeFilters = Object.assign({}, initialFilters);
    this.data = [];
  }

  clear() {
    this.data = [];
    this.activeFilters = Object.assign({}, initialFilters);
    this.responsibleOptions = [];
  }

  url(id?: number) {
    if (id) {
      return API_URL.TASKS.getTask(projectsDataStore.projectId, id);
    } else {
      return API_URL.TASKS.createTask(projectsDataStore.projectId);
    }
  }

  async init(isPrivate: boolean, taskListId: number) {
    try {
      this.isPrivate = isPrivate;
      this.taskListId = taskListId;
      await this.fetch();
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async fetch() {
    if (!this.taskListId) {
      return;
    }
    try {
      let url: string;
      const formBody = this.prepareRequestBody(0, this.PAGE_SIZE);
      if (this.isPrivate) {
        url = API_URL.TASKS.getPrivateTasksInList(projectsDataStore.projectId, this.taskListId);
      } else {
        url = API_URL.TASKS.getGlobalTasksInList(projectsDataStore.projectId, this.taskListId);
      }
      const response = await API.newData<API_TaskListContentResponse>(url, formBody);
      runInAction(() => {
        this.data = response.tasks;
        this.total = response.total;
        this.responsibleOptions = response.filterableResponsibles.map((i) => ({
          text: i.name,
          value: i.id.toString(),
        }));
      });
    } catch (error) {
      throw error;
    }
  }

  async loadMore(params: { limit: number; offset: number }) {
    console.log("loadMore", params);

    if (params.offset === 0) {
      return;
    }
    try {
      let url: string;
      const formBody = this.prepareRequestBody(params.offset, params.limit);
      if (this.isPrivate) {
        url = API_URL.TASKS.getPrivateTasksInList(projectsDataStore.projectId, this.taskListId);
      } else {
        url = API_URL.TASKS.getGlobalTasksInList(projectsDataStore.projectId, this.taskListId);
      }
      const response = await API.newData<API_TaskListContentResponse>(url, formBody);
      runInAction(() => {
        this.data = this.data.concat(response.tasks);
      });
    } catch (error) {
      throw error;
    }
  }

  toggleFilter(filter: TaskFilterType) {
    this.activeFilters[filter as string] = !this.activeFilters[filter];
    this.fetch();
  }

  setSorting(sortBy: keyof API_TaskData, sortOrder: "ASC" | "DESC") {
    if (sortBy === this.sortBy && sortOrder === this.sortOrder) {
      return;
    }
    this.sortBy = sortBy;
    this.sortOrder = sortOrder;
    this.fetch();
  }

  setFilters(filters: ActiveFilters) {
    this.activeFilters.responsibles = filters.responsibleName?.map((i) => +i);
    this.fetch();
  }

  get activeFiltersList(): Array<keyof API_TaskFilter> {
    return Object.entries(this.activeFilters).reduce<Array<keyof API_TaskFilter>>((list, [key, value]) => {
      if (value) {
        list.push(key as keyof API_TaskFilter);
      }
      return list;
    }, []);
  }

  async createTask(values: API_TaskData) {
    try {
      await API.newData(this.url(), values);
      this.fetch();
    } catch (err) {
      throw err;
    }
  }

  updateTaskInList(newTask: API_TaskData) {
    const foundIndex = this.data.findIndex((item) => newTask.id === item.id);
    this.data[foundIndex] = newTask;
  }

  taskIsEditable(id: number) {
    const task = this.data.find((i) => i.id === id);
    const currentId = +userInfoStore.id;
    return task.ownerId === currentId || task.responsibleId === currentId;
  }

  checkOwner(ownerId: number) {
    return ownerId === +userInfoStore.id;
  }

  prepareRequestBody(offset: number, limit: number): API_TaskRequest {
    const sortByMap: { [key in keyof API_TaskData]?: API_TaskPagination["sortBy"] } = {
      name: "NAME",
      responsibleName: "ASSIGNEE",
      dueDate: "DUE",
      priority: "PRIORITY",
      fulfillment: "PROGRESS",
      state: "STATE",
    };

    return {
      filter: {
        ...this.activeFilters,
      },
      pagination: {
        limit: limit,
        offset: typeof offset === "number" ? offset : this.data.length,
        sortBy: sortByMap[this.sortBy],
        sortOrder: this.sortOrder,
      },
    };
  }

  setDefaultFilters() {
    this.activeFilters = Object.assign({}, initialFilters);
  }
}

export const tasksDataStore = new TasksDataStore();
